export default {
  slug: "business-glossary-guide",
  path: "/blog/business-glossary-guide",
  kind: "guide",
  category: "Semantic layer",
  title: "How to Build a Business Glossary Your Team Actually Uses",
  seoTitle: "Business Glossary: How to Build One Teams Actually Use (2026 Guide)",
  description:
    "Most business glossaries die as wiki graveyards. The ones that survive are executable — wired into the query path. A practical guide to building and maintaining a glossary that stays alive.",
  keywords: "business glossary, data glossary, business glossary examples, data dictionary vs business glossary, glossary best practices",
  date: "2026-06-29",
  updated: "2026-07-07",
  author: "Sema Team",
  related: ["what-is-a-semantic-layer", "semantic-layer-vs-metrics-layer-vs-catalog", "semantic-layer-guide"],
  faq: [
    {
      q: "What's the difference between a data dictionary and a business glossary?",
      a: "A data dictionary describes technical objects (tables, columns, types) for engineers. A business glossary defines business terms ('active customer', 'net revenue') in business language, mapped to where they live in the schema. The glossary is the bridge between how the company talks and how the data is stored.",
    },
    {
      q: "Who should own the business glossary?",
      a: "Each term should have a domain owner (finance owns revenue terms, product owns engagement terms) with the data team owning the process and tooling. Glossaries owned solely by the data team drift from business reality; owned solely by the business, they drift from the schema.",
    },
    {
      q: "How many terms should a glossary start with?",
      a: "Twenty to fifty. Start with the terms that appear in exec reporting and the ones that cause recurring disputes. A glossary with 40 correct, used terms beats one with 500 stale entries — staleness anywhere breeds distrust everywhere.",
    },
  ],
  body: `
Every company has attempted a business glossary. It usually lives in Confluence, it was created with great energy during a "data culture" initiative, and it has not been opened since the quarter it was written. The pattern is so common it has a name in data teams: the wiki graveyard.

The problem was never effort or intent. It's that **documentation which nothing depends on will always go stale**. Here's how to build a glossary that can't rot — because it's load-bearing.

## The one rule: make it executable

A glossary entry that says *"Active customer: a customer with a qualifying subscription"* is prose. Nothing reads it, nothing enforces it, and when the schema changes, nothing notices.

An **executable** entry maps the term to the schema: \`active customer → customers.status = 'active' AND plan NOT IN ('trial')\`. Now the definition is used every time someone asks a question containing "active customer" — in chat, reports and alerts. Used definitions get corrected when wrong. Unused ones just decay quietly.

This is the core insight of the [semantic layer](/blog/what-is-a-semantic-layer): the glossary isn't documentation *about* the analytics system, it's a component *of* it.

## What a good entry contains

| Field | Why it matters |
| --- | --- |
| Term + aliases | "MAU", "monthly actives", "active users" resolve to one entry |
| Business definition | Plain-language, edge cases explicit (trials? refunds? test accounts?) |
| Schema mapping | Tables, columns and filters that implement it |
| Owner | The human who arbitrates disputes |
| Status | Draft → reviewed → approved; drafts are visible but flagged |
| Sensitivity | Whether the underlying columns are restricted (feeds [masking policy](/blog/pii-masking-ai-analytics)) |

## How to build it without a six-month project

**1. Draft automatically.** Modern tools profile your schema and generate draft definitions for detected entities and metrics. Machine drafts are imperfect — that's fine, they're drafts. Editing is 10x faster than authoring. (This is how [Sema seeds a glossary](/product) minutes after a source connects, and why it ships industry packs — SaaS, healthcare, finserv, retail — as starting points.)

**2. Prioritize by dispute, not by alphabet.** The terms worth defining first are the ones with a history: the metrics that differ between two dashboards, the words that mean different things in sales and finance. Ask "which numbers did we argue about this quarter?" and define those.

**3. Assign owners per domain.** Finance owns revenue terms; product owns engagement terms. The data team runs the process, not the definitions. An owner's job is small: approve drafts, arbitrate disputes, review changes — an hour a month.

**4. Wire it into the query path.** This is the step everyone skips and the only one that matters. When the glossary grounds real queries, three feedback loops turn on:

- **Wrong definitions surface fast** — someone asks about "churn", sees the SQL, spots the missing filter, and the fix propagates everywhere at once.
- **Coverage gaps announce themselves** — the system refuses questions using undefined terms, telling you exactly what to define next.
- **Staleness becomes impossible** — schema changes break mappings loudly instead of silently.

**5. Keep status visible.** Draft terms should answer questions with a visible "draft definition" flag. Perfect-or-nothing glossaries stall; visibly-improving ones build trust.

## The anti-patterns

- **The Big Bang** — defining 500 terms before launch. You'll be stale by term 300.
- **The committee definition** — six-week debates about "customer". Ship the finance version, note the dispute, move on; executable definitions can be changed in minutes.
- **The disconnected tool** — a standalone glossary app that isn't in the query path is a prettier wiki graveyard.

## Where this ends up

A glossary that grounds queries becomes something bigger: the vocabulary half of your semantic layer, alongside relationships, metrics and policy. That's the full architecture we cover in [The Semantic Layer: The Complete Guide](/semantic-layer) — and if you'd rather start from a machine-drafted glossary on your own schema than a blank page, [Sema's free tier does exactly that](/signup).
`,
};
